import type { initBlizzard, logEvent as logEventType } from '@snapchat/blizzard-lite';
import type { AnyEventBase, UserTrackedEvent, WebPlatformEvent } from '@snapchat/blizzard-schema';
import { parseError } from '@snapchat/core';
import type {
  BaseEventListenerProps,
  EmptyObject,
  LoggedEvent,
  WebTrackingPermission,
} from '@snapchat/logging';
import { AbstractEventListener, LoggingEventType } from '@snapchat/logging';

/** Base interface for any Web-driven event logs. */
export type BlizzardBaseEvent = UserTrackedEvent &
  AnyEventBase & {
    event_name: string;
  };

type BlizzardInitArgs = Parameters<typeof initBlizzard>[0];

export type BlizzardClientEventListenerProps<
  LoggingContext extends EmptyObject = EmptyObject,
  CustomEvents extends EmptyObject = EmptyObject
> = {
  isProd: boolean;
  cookieDomain: string;

  /**
   * Transforms from an event type on the event bus to a typed Blizzad Event.
   *
   * Because all blizzard event are custom, there's nothing generic that this listener can set
   * except pass the data from the formatter directly to the blizzard collector.
   */
  eventFormatter: (
    event: LoggedEvent<CustomEvents>,
    context: Partial<LoggingContext>
  ) => BlizzardBaseEvent[] | BlizzardBaseEvent | undefined;
} & BaseEventListenerProps<LoggingContext, CustomEvents> &
  Omit<BlizzardInitArgs, 'appEnvironment' | 'cookieArgs' | 'debugLoggingEnabled' | 'mode'>;

export class BlizzardClientEventListener<
  LoggingContext extends EmptyObject = EmptyObject,
  CustomEvents extends EmptyObject = EmptyObject,
  LoggingPermission extends WebTrackingPermission = WebTrackingPermission
> extends AbstractEventListener<LoggingContext, CustomEvents, LoggingPermission> {
  protected readonly props: BlizzardClientEventListenerProps<LoggingContext, CustomEvents>;

  protected logBlizzardEvent?: typeof logEventType;

  constructor(props: BlizzardClientEventListenerProps<LoggingContext, CustomEvents>) {
    super({
      ...props,
      requiredPermissions: ['logging', 'performance'],
    });
    this.props = props;
    this.init = this.init.bind(this); // So this is usable when inherited.
    this.logEventInternal = this.logEventInternal.bind(this);
  }

  /**
   * Written as a member instead of a const so it can be called by inherited fns.
   *
   * @override
   */
  protected async init(): Promise<void> {
    // NOTE: We import the blizzad dynamically here because it depends on some
    // packages like @snapchat/web-i18n and uuid that do not import well
    // and break Rollup and Jest and it allows the package to be put into
    // a splitpoint when loading.
    const { initBlizzard, logEvent, CollectionMode } = await import('@snapchat/blizzard-lite');

    await initBlizzard({
      ...this.props,
      appEnvironment: {
        appType: 'WEB_DESKTOP', // For web logging this is always WEB_DESKTOP.
        appBuild: 'PROD', // For web logging the Snapchat App is always PROD.
      },
      cookiesArgs: {
        customDomain: this.props.isProd ? this.props.cookieDomain : undefined,
      },
      debugLoggingEnabled: false, // Use console logger.
      mode: this.props.isProd ? CollectionMode.PROD : CollectionMode.STAGE,
    });

    this.logBlizzardEvent = logEvent;
  }

  /** @override */
  protected logEventInternal(
    event: LoggedEvent<CustomEvents>,
    context: Partial<LoggingContext>
  ): void {
    const formattedEvents = this.props.eventFormatter(event, context);

    if (!formattedEvents) return;

    const formattedEventsArray = Array.isArray(formattedEvents)
      ? formattedEvents
      : [formattedEvents];

    for (const formattedEvent of formattedEventsArray) {
      // We do not catch any errors here. Blizzard should have their own logging.
      void this.logBlizzardEvent!(formattedEvent);
    }
  }

  /** @override */
  protected flushInternal: () => Promise<unknown> = () => Promise.resolve();
}

export const formatWebPlatformEvent = (
  event: LoggedEvent<EmptyObject>,
  _context: EmptyObject
): BlizzardBaseEvent | undefined => {
  const uri = new URL(window.location.href);

  const data: WebPlatformEvent = {
    event_name: 'WEB_PLATFORM_EVENT',
    host: uri.host,
    path: uri.pathname,
    query: JSON.stringify(Object.fromEntries(uri.searchParams.entries())),
  };

  switch (event.type) {
    case LoggingEventType.DEBUG: {
      return {
        ...data,
        type: LoggingEventType.DEBUG,
        component: event.component,
        message: event.message,
      };
    }

    case LoggingEventType.ERROR: {
      const error = parseError(event.error);

      return {
        ...data,
        type: LoggingEventType.ERROR,
        component: event.component,
        message: error.message,
        action: event.action,
      };
    }

    case LoggingEventType.INFO: {
      return {
        ...data,
        type: LoggingEventType.INFO,
        component: event.component,
        action: event.action,
        label: event.label,
      };
    }

    case LoggingEventType.TIMING: {
      return {
        ...data,
        type: LoggingEventType.TIMING,
        component: event.component,
        variable: event.variable,
        value: event.valueMs.toString(),
        label: event.label,
      };
    }

    case LoggingEventType.VALUE: {
      return {
        ...data,
        type: LoggingEventType.VALUE,
        component: event.component,
        variable: event.variable,
        value: event.value.toString(),
        label: event.label,
      };
    }

    case LoggingEventType.WARNING: {
      return {
        ...data,
        type: LoggingEventType.WARNING,
        component: event.component,
        message: event.message,
        action: event.action,
      };
    }
  }

  return undefined;
};

/**
 * This table needs sensible privacy filters.
 *
 * Per blizzard privacy policy, you cannot store any personal information in the logs.
 *
 * See http://go.sc-corp.net/blizzard-privacy
 */
export function getCommonBlizzardClientEventListener<
  LoggingContext extends EmptyObject = EmptyObject,
  CustomEvents extends EmptyObject = EmptyObject,
  LoggingPermission extends WebTrackingPermission = WebTrackingPermission
>(
  props: BlizzardClientEventListenerProps
): BlizzardClientEventListener<LoggingContext, CustomEvents, LoggingPermission> {
  return new BlizzardClientEventListener<LoggingContext, CustomEvents, LoggingPermission>({
    isProd: props.isProd,
    cookieDomain: props.cookieDomain,
    eventFormatter: formatWebPlatformEvent,
  });
}
